import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import type { ConcentrationChartEntry } from '../../../utils/topClientsUtils';

interface ConcentrationChartProps {
  data: ConcentrationChartEntry[];
  topN: number;
  isFYQuarterMode: boolean;
  periodLabel: string;
}

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const ConcentrationTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ value: number; payload: ConcentrationChartEntry }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[140px]">
      <p className="text-[11px] font-bold text-slate-700 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-sm flex-none" style={{ backgroundColor: entry.color }} />
        <p className="text-[12px] font-semibold text-slate-800 tabular-nums">
          {payload[0].value.toFixed(1)}% revenue share
        </p>
      </div>
    </div>
  );
};

// ─── Main chart ───────────────────────────────────────────────────────────────
export const ConcentrationChart: React.FC<ConcentrationChartProps> = ({
  data,
  topN,
  isFYQuarterMode,
  periodLabel,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
    {/* Card header */}
    <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
      <div className="w-0.5 h-4 rounded-full bg-violet-500 flex-none" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
        Revenue Concentration
      </span>
      {isFYQuarterMode && (
        <>
          <span className="text-[10px] text-slate-400">·</span>
          <span className="text-[11px] text-slate-500 font-medium">{periodLabel}</span>
        </>
      )}
    </div>

    <div className="p-4">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748B' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => `${v.toFixed(0)}%`}
            tick={{ fontSize: 10, fill: '#94A3B8' }}
            axisLine={false}
            tickLine={false}
            width={40}
            domain={[0, 100]}
          />
          <Tooltip content={<ConcentrationTooltip />} cursor={{ fill: '#F8FAFC' }} />
          <Bar dataKey="percentage" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 justify-center">
        {data.map(entry => (
          <div key={entry.label} className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <div className="w-2.5 h-2.5 rounded-sm flex-none" style={{ backgroundColor: entry.color }} />
            <span>{entry.label}: {entry.percentage.toFixed(1)}%</span>
          </div>
        ))}
      </div>

      {/* Context note */}
      <p className="text-[10px] text-slate-500 text-center mt-3">
        Concentration of revenue across top {topN} clients
        {isFYQuarterMode ? ` for ${periodLabel}` : ' (lifetime)'}
      </p>
    </div>
  </div>
);

export default ConcentrationChart;
